import { useMemo } from 'react'

import { useCurrentOrgId, useIsCloaking } from '~/store/slices/currentOrg'

import type { FuelTankModel } from '../../client/ders'
import useManagedSites from './sites'
import useDataManager from './useDataManager'
import { EMPTY, useNormalizedFilters } from './utils'

type FuelTankFilters = {
  orgId?: string
  siteId?: string
  nodeId?: string
}

type UseManagedFuelTanksInput = {
  filters?: FuelTankFilters
}

export default function useManagedFuelTanks({ filters }: UseManagedFuelTanksInput = {}) {
  const { fuelTanks } = useDataManager()
  const { data, isLoading, isFetching } = fuelTanks

  // The manager fetches with `orgIds: [principalOrgId]` + a deep orgDepth,
  // so the cached set is already scoped to the principal user's org tree.
  // The site-join only does real narrowing when the caller wants a sub-slice
  // — i.e. while cloaking, or when an explicit `orgId` filter is passed.
  const isCloaking = useIsCloaking()
  const needsSiteJoin = isCloaking || filters?.orgId != null

  const {
    sites: orgSites,
    isLoading: isSitesLoading,
    isFetching: isSitesFetching
  } = useManagedSites({
    filters: { orgId: filters?.orgId }
  })

  return {
    fuelTanks: useFilteredFuelTanks(data, orgSites, needsSiteJoin, filters),
    isLoading: isLoading || (needsSiteJoin && isSitesLoading),
    isFetching: isFetching || (needsSiteJoin && isSitesFetching)
  }
}

function useFilteredFuelTanks(
  data: FuelTankModel[],
  orgSites: ReturnType<typeof useManagedSites>['sites'],
  needsSiteJoin: boolean,
  filters?: FuelTankFilters
) {
  const currentOrgId = useCurrentOrgId()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })

  const allowedSiteIds = useMemo(() => new Set(orgSites.map((site) => site.depotId)), [orgSites])

  return useMemo(() => {
    const { siteId, nodeId } = normalizedFilters

    if (siteId === EMPTY || nodeId === EMPTY) {
      return []
    }

    let filtered = needsSiteJoin ? data.filter((fuelTank) => allowedSiteIds.has(fuelTank.siteId)) : data

    if (siteId != null) {
      filtered = filtered.filter((fuelTank) => fuelTank.siteId === siteId)
    }

    if (nodeId != null) {
      filtered = filtered.filter((fuelTank) => fuelTank.nodeId === nodeId)
    }

    return filtered
  }, [data, allowedSiteIds, needsSiteJoin, normalizedFilters])
}
